import {
  ArrowsUpDownIcon,
  CalendarIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  UserIcon,
} from '@heroicons/react/20/solid';

/**
 * Type for the raw payload from the backend.
 * Backend uses 'old' and 'new' field names (not old_due_date/new_due_date).
 */
interface RawFieldChangePayload {
  old?: unknown;
  new?: unknown;
}

/**
 * Type for user objects in the assignee payload.
 */
interface UserPayload {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

/**
 * Type for status objects in the status change payload.
 */
interface StatusPayload {
  name?: string | null;
}

// --- Type Guards ---

/**
 * Type guard to check if a value is a StatusPayload object.
 */
const isStatusPayload = (value: unknown): value is StatusPayload => {
  if (typeof value !== 'object' || value === null) return false;
  return 'name' in value;
};

/**
 * Type guard to check if a value is a UserPayload object.
 */
const isUserPayload = (value: unknown): value is UserPayload => {
  if (typeof value !== 'object' || value === null) return false;
  // User payloads typically have at least one of these fields
  return 'first_name' in value || 'last_name' in value || 'email' in value;
};

/**
 * Type guard to check if a value is a string (for date payloads).
 */
const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};

/**
 * Type guard to check if a value is a number (for priority payloads).
 */
const isNumber = (value: unknown): value is number => {
  return typeof value === 'number';
};

/**
 * Configuration for a task field change event.
 * Defines how to render the event in both standalone and correlated views.
 */
export interface TaskFieldChangeConfig {
  /** Event type discriminator */
  type: 'field_change';
  /** Heroicon component to display */
  icon: typeof CheckCircleIcon;
  /** Human-readable title (e.g., "Status Changed") */
  title: string;
  /** Field label (e.g., "Status") */
  label: string;
  /** Format the old value for display */
  formatOldValue: (payload: RawFieldChangePayload) => string;
  /** Format the new value for display */
  formatNewValue: (payload: RawFieldChangePayload) => string;
}

/**
 * Payload structure for task creation events.
 */
interface TaskCreatedPayload {
  task?: {
    due_date?: string | null;
    name?: string;
  };
}

/**
 * Configuration for a task creation event.
 * Unlike field changes, creation events don't have old/new values.
 */
export interface TaskCreationConfig {
  /** Event type discriminator */
  type: 'creation';
  /** Heroicon component to display */
  icon: typeof PlusCircleIcon;
  /** Human-readable title (e.g., "Task Created") */
  title: string;
  /** Format additional details for display (e.g., due date) */
  formatDetails: (payload: TaskCreatedPayload) => string | null;
}

/**
 * Union type for all task event configurations.
 */
export type TaskEventConfig = TaskFieldChangeConfig | TaskCreationConfig;

// --- Helper Functions ---

/**
 * Format a user object to a display name.
 */
const formatUserName = (user: UserPayload | null | undefined): string => {
  if (!user) return 'Unassigned';
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fullName || user.email || 'Unknown User';
};

/**
 * Format a date string to a human-readable format.
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'None';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format a priority value (order number) to human-readable label.
 * Priority is based on the task's `order` field:
 * - order <= -100: Immediate
 * - -100 < order < 0: Urgent
 * - order >= 0: Normal
 */
const formatPriority = (priority: number | null | undefined): string => {
  if (priority === null || priority === undefined) return 'None';
  if (priority <= -100) return 'Immediate';
  if (priority < 0) return 'Urgent';
  return 'Normal';
};

// --- Configuration Registry ---

/**
 * Configuration for task events.
 * Maps event_code to rendering configuration.
 *
 * Field change events have:
 * - type: 'field_change'
 * - icon: Visual indicator for the change type
 * - title: Human-readable heading
 * - label: Field label for forms
 * - formatOldValue/formatNewValue: Type-safe value formatters
 *
 * Creation events have:
 * - type: 'creation'
 * - icon: Visual indicator
 * - title: Human-readable heading
 * - formatDetails: Optional details formatter
 */
export const TASK_EVENT_CONFIG: Record<string, TaskEventConfig> = {
  task_status_changed: {
    type: 'field_change',
    icon: CheckCircleIcon,
    title: 'Status Changed',
    label: 'Status',
    formatOldValue: (payload) => {
      if (!isStatusPayload(payload.old)) return 'None';
      return payload.old.name || 'None';
    },
    formatNewValue: (payload) => {
      if (!isStatusPayload(payload.new)) return 'None';
      return payload.new.name || 'None';
    },
  },

  task_assigned: {
    type: 'field_change',
    icon: UserIcon,
    title: 'Assignee Changed',
    label: 'Assignee',
    formatOldValue: (payload) => {
      if (!isUserPayload(payload.old)) return 'Unassigned';
      return formatUserName(payload.old);
    },
    formatNewValue: (payload) => {
      if (!isUserPayload(payload.new)) return 'Unassigned';
      return formatUserName(payload.new);
    },
  },

  task_due_date_changed: {
    type: 'field_change',
    icon: CalendarIcon,
    title: 'Due Date Changed',
    label: 'Due Date',
    formatOldValue: (payload) => {
      if (!isString(payload.old) && payload.old !== null) return 'None';
      return formatDate(payload.old as string | null);
    },
    formatNewValue: (payload) => {
      if (!isString(payload.new) && payload.new !== null) return 'None';
      return formatDate(payload.new as string | null);
    },
  },

  task_priority_changed: {
    type: 'field_change',
    icon: ArrowsUpDownIcon,
    title: 'Priority Changed',
    label: 'Priority',
    formatOldValue: (payload) => {
      if (!isNumber(payload.old) && payload.old !== null) return 'None';
      return formatPriority(payload.old as number | null);
    },
    formatNewValue: (payload) => {
      if (!isNumber(payload.new) && payload.new !== null) return 'None';
      return formatPriority(payload.new as number | null);
    },
  },

  task_created: {
    type: 'creation',
    icon: PlusCircleIcon,
    title: 'Task Created',
    formatDetails: () => null,
  },
};

/**
 * Backwards compatibility alias.
 * @deprecated Use TASK_EVENT_CONFIG instead
 */
export const TASK_FIELD_CHANGE_CONFIG = TASK_EVENT_CONFIG;

/**
 * Check if an event code has a configuration (field change or creation).
 */
export const isConfiguredTaskEvent = (eventCode: string): boolean =>
  eventCode in TASK_EVENT_CONFIG;

/**
 * Backwards compatibility alias.
 * @deprecated Use isConfiguredTaskEvent instead
 */
export const isConfiguredFieldChangeEvent = isConfiguredTaskEvent;

/**
 * Get the configuration for a task event.
 * Returns undefined if the event code is not configured.
 */
export const getTaskEventConfig = (
  eventCode: string,
): TaskEventConfig | undefined => TASK_EVENT_CONFIG[eventCode];

/**
 * Get the configuration for a field change event specifically.
 * Returns undefined if the event code is not configured or is not a field change.
 */
export const getFieldChangeConfig = (
  eventCode: string,
): TaskFieldChangeConfig | undefined => {
  const config = TASK_EVENT_CONFIG[eventCode];
  if (config?.type === 'field_change') {
    return config;
  }
  return undefined;
};

/**
 * Get the configuration for a creation event specifically.
 * Returns undefined if the event code is not configured or is not a creation event.
 */
export const getCreationConfig = (
  eventCode: string,
): TaskCreationConfig | undefined => {
  const config = TASK_EVENT_CONFIG[eventCode];
  if (config?.type === 'creation') {
    return config;
  }
  return undefined;
};
